import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import { meetsRequiredRules } from '@eliadmin/core/src/components/nt/account/password/password-rules';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

/** Message of the „rules not met“ issue: the rule list under the field shows it, not an error line. */
export const PASSWORD_RULES_ISSUE = 'password-rules';

/**
 * Body of the core `changePassword` (`changePasswordSchema`). The new password has to meet the
 * required admin rules – shown red in the list under the field (`PASSWORD_RULES_ISSUE`) – and differ from the current one, as the core form checks („Nové heslo sa musí líšiť od
 * starého“).
 */
export const passwordSchema = (
  VM: FormValidationMessages,
  rulesText: string,
  sameAsCurrentMessage: string,
) =>
  __eliadmin_zod_0({
      password_old: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_1(),
      password_confirmation: __eliadmin_zod_1(),
    })
    .superRefine((values, ctx) => {
      const issue = (path: 'password' | 'password_confirmation', message: string) =>
        ctx.addIssue({ code: 'custom', path: [path], message });

      if (!values.password) issue('password', VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
      else if (!rulesText && values.password.length < 8) {
        // No rules in the administration: the core minimum.
        issue('password', VM.MIN_LENGTH_VALIDATION_MESSAGE(8));
      } else if (!meetsRequiredRules(rulesText, values.password))
        issue('password', PASSWORD_RULES_ISSUE);
      else if (values.password === values.password_old) issue('password', sameAsCurrentMessage);

      if (!values.password_confirmation) {
        issue('password_confirmation', VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
      } else if (values.password_confirmation !== values.password) {
        issue('password_confirmation', VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE());
      }
    });

export type PasswordFormValues = z.infer<ReturnType<typeof passwordSchema>>;
